import { BalanceData } from "../data/balance";
import { OfferDef } from "../data/offers";
import { SeasonTheme, SeasonsData } from "../data/seasons";
import { periodOf } from "./deals";
import { seasonAt, seasonTheme } from "./season";

/**
 * The season skins after their season (docs/SHOP.md §15): what the cosmetic tier pays when the season list repeats and
 * the skin is already owned, and the rotation of past seasons' skins into the Master's Collection. Pure date math from
 * the epoch (seasons.json), no server.
 */

/** What the cosmetic tier pays in skin: the season's skin, or (already owned) `repeatSkinGems` gems instead. */
export interface CosmeticSkinPay {
  /** The skin it unlocks ("" for none). */
  readonly skin: string;
  /** Gems paid instead of a skin already owned (0 otherwise). */
  readonly gems: number;
}

/** The cosmetic tier of a season with `theme`: its skin, or its gem-equivalent when the player owns it already. */
export function cosmeticSkinPay(data: SeasonsData, theme: SeasonTheme, owned: boolean): CosmeticSkinPay {
  if (theme.skin === "") return { skin: "", gems: 0 };
  return owned ? { skin: "", gems: data.repeatSkinGems } : { skin: theme.skin, gems: 0 };
}

/**
 * The season skins on rotation into the Master's Collection at `now`, in season order: those whose season (shop.json
 * `season`, 1-based) has ended and after which at least `mastersRotation.afterSeasons` more full seasons have passed —
 * Season n's skin from (0-based) season n + afterSeasons on. Never the running season's own skin (a later loop of the
 * list pays it on the pass).
 */
export function rotatingSeasonSkins(balance: BalanceData, now: number): string[] {
  const d = balance.seasons;
  if (d.tiers <= 0 || d.mastersRotation.offer === "") return [];
  const current = seasonAt(d, now);
  const running = seasonTheme(d, current).skin;
  const out: { id: string; season: number }[] = [];
  for (const k of balance.commerce.shop.skins.list) {
    if (k.unlock.by === "season" && current >= k.unlock.season + d.mastersRotation.afterSeasons && k.id !== running) out.push({ id: k.id, season: k.unlock.season });
  }
  return out.sort((a, b) => a.season - b.season).map((k) => k.id);
}

/** The season skin the Master's Collection offers at `now` ("" for none): the rotation's skins take turns, one a fortnight. */
export function mastersSeasonSkin(balance: BalanceData, now: number): string {
  const list = rotatingSeasonSkins(balance, now);
  return list.length === 0 ? "" : list[periodOf("fortnight", now).key % list.length];
}

/** A catalog offer as sold at `now`: the rotation's offer has its skin prize replaced by this fortnight's season skin. */
export function offerWithSeasonSkin(balance: BalanceData, def: OfferDef, now: number): OfferDef {
  if (def.id !== balance.seasons.mastersRotation.offer) return def;
  const skin = mastersSeasonSkin(balance, now);
  if (skin === "") return def;
  return { ...def, prizes: def.prizes.map((p) => (p.kind === "skin" ? { kind: "skin", skinId: skin, count: 1 } : p)) };
}
